// Client entry — wires Three.js scene, WebSocket net, ClientStore, and panels.
// Embodies the "grammar IS the game" UI rule: only the panel matching the
// current phase is rendered. No always-on HUD until you're actually playing.

import { Scene } from "./client/scene.js";
import { Net } from "./client/net.js";
import { ClientStore, type ClientState } from "./client/state.js";
import { renderLoginPanel } from "./client/panels/login.js";
import { renderCharacterCreate } from "./client/panels/character_create.js";
import { renderMudPanel } from "./client/panels/mud.js";
import { mountMinimap } from "./client/panels/minimap.js";
import { mountFx } from "./client/panels/fx.js";
import { loadManifest } from "./client/asset_loader.js";
import { mountHudBar } from "./client/panels/hud_bar.js";
import { mountTradePanelAutoShow } from "./client/panels/trade.js";
import { mountAccountWidget } from "./client/panels/account_widget.js";
import { mountCombatPanel } from "./client/panels/combat.js";
import { mountHotbar, ensureHotbarStyles } from "./client/panels/hotbar.js";

// Create the store/net FIRST — panels and overlays reference them.
const store = new ClientStore();
const net = new Net();

const sceneHost = document.getElementById("scene-layer");
if (!sceneHost) throw new Error("main: #scene-layer missing");
const scene = new Scene(sceneHost);
scene.start();

const uiHost = document.getElementById("ui-layer");
if (!uiHost) throw new Error("main: #ui-layer missing");
const minimapHost = document.getElementById("minimap-host");
if (minimapHost) mountMinimap(minimapHost, store);
const fxHost = document.getElementById("fx-overlay");
const hudNet   = document.getElementById("hud-net");
const hudHb    = document.getElementById("hud-hb");
const hudLat   = document.getElementById("hud-lat");
const hudScene = document.getElementById("hud-scene");
if (hudScene) hudScene.textContent = "running";

// Status updates -> HUD
net.onStatus((s) => {
  if (!hudNet) return;
  hudNet.textContent = s;
  hudNet.className = "v " + (s === "open" ? "green" : s === "closed" ? "red" : "gold");
});

// Server messages -> store; track heartbeat in HUD
let lastHb = 0;
net.on((msg) => {
  if (msg.t === "heartbeat") {
    lastHb = msg.n;
    if (hudHb)  hudHb.textContent  = String(msg.n);
    if (hudLat) hudLat.textContent = `${Math.max(0, Date.now() - msg.server_time)} ms`;
    return;
  }
  // Apply atmosphere when the room view changes.
  if (msg.t === "room" && msg.view.atmosphere) {
    scene.applyAtmosphere(msg.view.atmosphere);
    // Sync 3D actors (mobs + NPCs that have model URLs). Text chips in the
    // room panel always render — the scene is bonus chrome. Laid out along
    // a horizontal arc so they don't overlap.
    const view = msg.view;
    const desired: Array<{ key: string; url: string; x?: number; z?: number }> = [];
    const mobs = view.mobs ?? [];
    mobs.forEach((m, i) => {
      if (m.model_url) desired.push({ key: `mob:${m.id}`, url: m.model_url, x: (i - (mobs.length - 1) / 2) * 1.8, z: -0.5 });
    });
    const npcs = view.npcs ?? [];
    npcs.forEach((n, i) => {
      // NPC model_url field exists only if exposed via content override; the
      // protocol carries portrait_url by default. A future tick can add it.
      const anyN = n as { model_url?: string };
      if (anyN.model_url) desired.push({ key: `npc:${n.id}`, url: anyN.model_url, x: (i - (npcs.length - 1) / 2) * 1.8, z: 0.5 });
    });
    void scene.syncActors(desired);
  }
  store.apply(msg);
  // After we get hello, attempt resume if we have a saved token; otherwise list/login.
  if (msg.t === "hello") {
    const token = store.restoreToken();
    if (token) net.send({ t: "resume", token });
  }
});

// Phase -> which panel renders
let lastPhase: string | null = null;
let lastCharCount = -1;
let lastRoomKey = "";
let lastLogLen  = -1;
store.subscribe((state: ClientState) => {
  // Re-render only when something the panel cares about changes.
  const charCount = state.characters.length;
  const roomKey = state.room ? `${state.room.zone}/${state.room.room}/${state.room.others.join(",")}` : "";
  const phaseChanged = state.phase !== lastPhase;
  if (state.phase === "need_auth" && phaseChanged) {
    renderLoginPanel(uiHost, net);
  } else if (state.phase === "need_character" && (phaseChanged || charCount !== lastCharCount)) {
    renderCharacterCreate(uiHost, net, state);
  } else if (state.phase === "playing" && phaseChanged) {
    renderMudPanel(uiHost, net, store);
  }
  lastPhase = state.phase;
  lastCharCount = charCount;
  lastRoomKey = roomKey;
  lastLogLen = state.log.length;
});

if (fxHost) mountFx(fxHost, net);
mountHudBar(uiHost, net, store);
mountTradePanelAutoShow(uiHost, net, store);
mountAccountWidget(uiHost, net, store);
mountCombatPanel(uiHost, net, store);
ensureHotbarStyles();
mountHotbar(uiHost, net, store);

// Load the asset manifest before opening the socket so first-frame panels
// can resolve art synchronously. Manifest load is best-effort; missing => placeholders.
loadManifest().finally(() => net.connect());
void lastHb; void lastRoomKey; void lastLogLen; // satisfy strict unused warnings if any
